import { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import { Monitor, Users, Calendar, Lock, Clock } from "lucide-react";
import { useMutation } from "@apollo/client";
import { ADD_CAMPAIGN_CART } from "@/Graphql/packageQurie";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/utils/authContext";
import { useCart, type CartItem } from "@/utils/cartContext";


// Set Mapbox access token
mapboxgl.accessToken = 'pk.eyJ1IjoidGVqYS1zaHJlZSIsImEiOiJjbHo4OWF6ZHgwMGwxMmxzYWxzaTk1YnNiIn0.hpbbzYRq7WOaOPwInekf9w';

interface MapViewProps {
  inventory: any[];
  onInventorySelect: (inventoryItem: any) => void;
  onAddToCart?: (inventoryItem: any) => void;
  onDeleteFromCart?: (inventoryItem: any) => void;
  isInCart?: (id: number | string) => boolean;
  currencyCode?: string;
  edit?: boolean; // Optional: for edit mode
  draftData?: any; // Optional: for draft data
  cartDataList?: any[]; // Optional: for cart data list
  loadingCartItemIds?: any;
  campaignStartDate?: Date | null; // Campaign start date for package validation
  campaignEndDate?: Date | null; // Campaign end date for package validation
}

const formatNumber = (num: number | undefined | null): string => {
  if (!num || typeof num !== 'number') return '0';
  if (num >= 1000000) {
    return (num / 1000000).toFixed(1) + 'M';
  } else if (num >= 1000) {
    return (num / 1000).toFixed(1) + 'K';
  } else {
    return num.toString();
  }
};

export default function MapView({ inventory, onInventorySelect, onAddToCart, onDeleteFromCart, isInCart, currencyCode, edit, draftData, cartDataList, loadingCartItemIds, campaignStartDate, campaignEndDate }: MapViewProps) {

  const base_path = import.meta.env.VITE_APP_BASE_PATH || '';
  const { isLoggedIn } = useAuth();
  const navigate = useNavigate();
  const [mapType, setMapType] = useState<'map' | 'satellite'>('map');
  const [selectedInventoryItem, setSelectedInventoryItem] = useState<any>(null);
  const [selectedPackageId, setSelectedPackageId] = useState<string | null>(null);
  const mapContainer = useRef<HTMLDivElement>(null);
  const map = useRef<mapboxgl.Map | null>(null);
  const [mapLoaded, setMapLoaded] = useState(false);
  const markersRef = useRef<mapboxgl.Marker[]>([]);
  const [mapMode, setMapMode] = useState<'map' | 'satellite'>('map');
  const [selectedMarkerId, setSelectedMarkerId] = useState<string | null>(null);
  const [cartActionTrigger, setCartActionTrigger] = useState(0); // New state to trigger popup updates

  // Package cart functionality
  const { addToCart, removeFromCart, priceFormat } = useCart();
  const { toast } = useToast();
  const [addCampaignCart, { loading: addCartLoading }] = useMutation(ADD_CAMPAIGN_CART);

  // Palette used to color duplicate markers at same coordinates (pink replaces yellow)
  const duplicatePalette = ['#22c55e', '#3b82f6', '#f660c1ff', '#ef4444', '#8b5cf6'];
  const duplicatePink = '#f660c1ff';

  // Group markers by coordinate, and track cycle index per coordinate
  const coordGroupsRef = useRef<Map<string, Array<{ marker: mapboxgl.Marker; inventoryItem: any; billboard?: any; isPackage: boolean }>>>(new Map());
  const coordCycleIndexRef = useRef<Map<string, number>>(new Map());

  // Update the visible badge and tooltip for a coordinate group
  // Only show duplicate badges/tooltips for package entries. Regular inventory groups will not display badges.
  const updateGroupLabels = (key: string, currentIndex?: number) => {
    const group = coordGroupsRef.current.get(key) || [];

    // Only consider package entries for duplicate display
    const packageEntries = group.filter(e => !!e.isPackage);
    const packageCount = packageEntries.length;

    if (packageCount > 1) {
      // compute display index: use supplied currentIndex, else use stored cycle index or 1
      const stored = coordCycleIndexRef.current.get(key) || 0;
      const displayIndex = currentIndex !== undefined ? currentIndex : ((stored % packageCount) + 1);

      // Add/update badge and tooltip for package markers only
      packageEntries.forEach((entry) => {
        const el = entry.marker.getElement() as HTMLElement;
        if (!el) return;

        el.setAttribute('title', `${packageCount} billboards - showing ${displayIndex} of ${packageCount}. Click to view next`);
        let badge = el.querySelector<HTMLDivElement>('.duplicate-count');
        if (!badge) {
          badge = document.createElement('div');
          badge.className = 'duplicate-count';
          badge.style.position = 'absolute';
          badge.style.top = '-6px';
          badge.style.right = '-6px';
          badge.style.minWidth = '18px';
          badge.style.height = '18px';
          badge.style.padding = '0 5px';
          badge.style.display = 'flex';
          badge.style.alignItems = 'center';
          badge.style.justifyContent = 'center';
          badge.style.fontSize = '11px';
          badge.style.fontWeight = '600';
          badge.style.color = 'white';
          // badge background MUST always be pink per requirement
          badge.style.background = duplicatePink;
          badge.style.borderRadius = '999px';
          badge.style.boxShadow = '0 1px 3px rgba(0,0,0,0.2)';
          badge.style.pointerEvents = 'none';
          el.appendChild(badge);
        }
        badge.textContent = String(packageCount);

        // DO NOT change individual marker color here — keep sold-out markers red.
      });

      // Ensure non-package markers in the same coord group do not show badges or titles
      group.forEach((entry) => {
        if (!entry.isPackage) {
          const el = entry.marker.getElement() as HTMLElement;
          if (!el) return;
          el.removeAttribute('title');
          const badge = el.querySelector('.duplicate-count');
          if (badge) badge.remove();
        }
      });
    } else {
      // No package duplicates - remove badges/titles from all markers in this group
      group.forEach((entry) => {
        const el = entry.marker.getElement() as HTMLElement;
        if (!el) return;
        el.removeAttribute('title');
        const badge = el.querySelector('.duplicate-count');
        if (badge) badge.remove();
      });
    }
  };

  // Helper to deep remove __typename recursively (needed for GraphQL API)
  const removeTypename = (obj: any): any => {
    if (Array.isArray(obj)) return obj.map(removeTypename);
    if (obj && typeof obj === 'object') {
      const newObj: any = {};
      for (const key in obj) {
        if (key !== '__typename') {
          newObj[key] = removeTypename(obj[key]);
        }
      }
      return newObj;
    }
    return obj;
  };

  // Handle package add to cart - matching PackageCard format exactly
  const handlePackageAddToCart = async (pkg: any) => {
    if (!pkg) return false;

    try {
      // Build input for mutation exactly like PackageCard - as an array
      const input = [{
        accessToken: localStorage.getItem('authToken') || '',
        userId: localStorage.getItem('userId') || '',
        campaignName: pkg.name || '',
        startDate: pkg.validityStartDate || pkg.startDate || '',
        endDate: pkg.validityEndDate || pkg.endDate || '',
        cartItemName: pkg.name || '',
        cartItemId: pkg.id || '',
        cartItemReferenceId: null,
        cartItemType: 'PACKAGE',
        cartItemCountry: pkg.country?.name || '',
        cartItemVenueType: null,
        cartItemResolution: '',
        cartItemThumbnailUrl: pkg.thumbnail || '',
        cartItemLatitude: null,
        cartItemLongitude: null,
        packageDetails: {
          ...removeTypename({
            id: pkg.id || '',
            name: pkg.name || '',
            description: pkg.description || '',
            country: pkg.country ? {
              id: pkg.country.id || '',
              name: pkg.country.name || '',
              timezone: pkg.country.timezone || ''
            } : null,
            publisher: pkg.publisher ? {
              id: pkg.publisher.id || '',
              name: pkg.publisher.name || ''
            } : null,
            startDate: pkg.startDate || '',
            endDate: pkg.endDate || '',
            validityStartDate: pkg.validityStartDate || '',
            validityEndDate: pkg.validityEndDate || '',
            registryPackageType: pkg.registryPackageType || '',
            campaignDuration: pkg.campaignDuration || 0,
            purchaseLimitation: pkg.purchaseLimitation || 0,
            numberOfPurchase: pkg.numberOfPurchase || 0,
            inventory: pkg.inventory || {
              type: [],
              total: 0,
              classic: 0,
              digital: 0
            },
            exposure: pkg.exposure || [],
            currency: pkg.currency || '',
            price: pkg.price || 0,
            discount: pkg.discount || 0,
            totalPrice: pkg.totalPrice || 0,
            thumbnail: pkg.thumbnail || '',
            estimation: pkg.estimation || {
              potentialViews: 0,
              uniqueReach: 0,
              avgFrequency: 0,
              cpm: 0,
              spotsPerHour: 0,
              totalSpots: 0
            },
            inventories: pkg.inventories || [],
            publishTo: pkg.publishTo || [],
            tagIds: [],
            tags: pkg.tags || [],
            reopened: pkg.reopened || false,
            createdBy: pkg.createdBy || '',
            createdDate: pkg.createdDate || '',
            lastModifiedBy: pkg.lastModifiedBy || '',
            lastModifiedDate: pkg.lastModifiedDate || '',
            discountPrice: pkg.discountPrice || {
              originalPrice: 0,
              totalPrice: 0,
              discountPrice: 0
            },
            loadingicon: pkg.loadingicon || false,
            cartAdded: pkg.cartAdded || false,
            displayCount: pkg.displayCount || 0,
            showlocation: pkg.showlocation || false,
            packageexpire: pkg.packageexpire || 0,
            packagescreens: pkg.packagescreens || 0,
            packagediscountPrice: pkg.packagediscountPrice || 0,
            type: 'PACKAGE'
          })
        },
        discount: pkg.discount ? (pkg.price * pkg.discount) / 100 : 0,
        nowPayPrice: pkg.totalPrice || 0,
        totalPrice: pkg.price || 0,
        spotDuration: null
      }];

      const { data } = await addCampaignCart({ variables: { input } });
      const itemDataArr = data?.addCampaignCart?.itemData;
      const itemData = Array.isArray(itemDataArr) ? itemDataArr[0] : undefined;

      if (itemData && itemData.success) {
        toast({
          title: 'Added to cart',
          description: `${pkg.name} added to cart successfully!`,
        });

        // Update cart context for cart count
        addToCart({
          id: pkg.id,
          name: pkg.name,
          location: pkg.country?.name || '',
          state: '',
          price: (pkg.totalPrice || 0) * 100, // Convert to cents for cart context
          impressions: pkg.estimation?.potentialViews || 0,
          uniqueVisitors: pkg.monthlySummary?.uniqueVisitors || 0,
          frequency: pkg.monthlySummary?.frequency || 0,
          size: (pkg.inventory?.total || '').toString(),
          type: 'Package',
          status: (pkg.numberOfPurchase < pkg.purchaseLimitation) ? 'available' : 'sold-out',
          image: pkg.thumbnail || `${import.meta.env.VITE_APP_FOLDER_PATH}/nomedia.jpg`,
          spots: undefined,
          campaignDuration: pkg.campaignDuration || undefined,
          dailyHours: undefined,
          isDeal: false,
          daypartSchedules: undefined,
          packageStartDate: pkg.startDate,
          packageEndDate: pkg.endDate,
          requiresCampaignDuration: false,
          packageDetails: pkg,
          currencyCode: pkg?.price?.localCurrencies?.code || 'N/A',
          specifications: {
            resolution: 'Various',
            duration: `${pkg.startDate} - ${pkg.endDate}`
          },
          priceFormat: priceFormat
        });

        // Update package cart state optimistically in the inventory array
        const inventoryIndex = inventory.findIndex(item => item.id === pkg.id);
        if (inventoryIndex !== -1) {
          inventory[inventoryIndex] = {
            ...inventory[inventoryIndex],
            _isInCart: true,
            isInCart: true
          };
        }

        // Also update the package object reference
        pkg._isInCart = true;
        pkg.isInCart = true;

        // Trigger cart action for popup updates
        setCartActionTrigger(prev => prev + 1);
        return true;
      } else {
        const errorMessage = (itemData && itemData.message) || 'Failed to add package to cart.';
        toast({
          title: 'Error',
          description: errorMessage,
          variant: 'destructive',
        });
        return false;
      }
    } catch (err: any) {
      console.error('Package add to cart error:', err);
      toast({
        title: 'Error',
        description: err.message || 'An unexpected error occurred while adding package to cart.',
        variant: 'destructive',
      });
      return false;
    }
  };

  // Handle package remove from cart
  const handlePackageRemoveFromCart = async (pkg: any) => {
    if (!pkg) return false;

    console.log(`[PACKAGE REMOVE] Starting removal for package ${pkg.id}:`, pkg.name);

    try {
      // Use the parent's onDeleteFromCart function if available
      if (onDeleteFromCart) {
        console.log(`[PACKAGE REMOVE] Calling onDeleteFromCart for package ${pkg.id}`);
        await onDeleteFromCart(pkg);

        // Remove from cart context for cart count update
        removeFromCart(pkg.id);

        // Update package cart state optimistically in the inventory array
        const inventoryIndex = inventory.findIndex(item => item.id === pkg.id);
        if (inventoryIndex !== -1) {
          inventory[inventoryIndex] = {
            ...inventory[inventoryIndex],
            _isInCart: false,
            isInCart: false
          };
        }

        // Also update the package object reference
        pkg._isInCart = false;
        pkg.isInCart = false;

        console.log(`[PACKAGE REMOVE] Successfully removed package ${pkg.id} from cart`);

        toast({
          title: 'Removed from cart',
          description: `${pkg.name} removed from cart successfully!`,
        });

        // Trigger cart action for popup updates
        setCartActionTrigger(prev => prev + 1);
        return true;
      } else {
        toast({
          title: 'Error',
          description: 'Remove from cart function not available.',
          variant: 'destructive',
        });
        return false;
      }
    } catch (err: any) {
      console.error('Package remove from cart error:', err);
      toast({
        title: 'Error',
        description: err.message || 'An unexpected error occurred while removing package from cart.',
        variant: 'destructive',
      });
      return false;
    }
  };

  // Helper function to check if package is available based on campaign dates
  const isPackageAvailable = (pkg: any): boolean => {
    if (!campaignStartDate || !campaignEndDate) return true; // If no campaign dates, assume available

    const validityStart = pkg.startDate ? new Date(pkg.startDate) : null;
    const validityEnd = pkg.endDate ? new Date(pkg.endDate) : null;

    if (!validityStart || !validityEnd) return true; // If no validity dates, assume available

    // Package is available if its validity period overlaps with or is within campaign period
    return validityStart <= campaignEndDate && validityEnd >= campaignStartDate;
  };

  // Center map on selected inventory item
  const handleInventorySelect = (inventoryItem: any) => {
    const isPackage = inventoryItem.inventory || inventoryItem.inventories || inventoryItem.registryPackageType || inventoryItem.totalPrice !== undefined;

    setSelectedInventoryItem(inventoryItem);

    if (isPackage) {
      // For packages, set the selected package ID to show all billboard locations
      setSelectedPackageId(inventoryItem.id);

      // Navigate to show all billboard locations for this package
      if (inventoryItem.inventories && inventoryItem.inventories.length > 0) {
        // Calculate bounds for all billboards in the package
        const packageCoordinates: [number, number][] = [];
        inventoryItem.inventories.forEach((billboard: any) => {
          if (billboard.latitude && billboard.longitude &&
            !isNaN(parseFloat(billboard.latitude)) && !isNaN(parseFloat(billboard.longitude))) {
            packageCoordinates.push([parseFloat(billboard.longitude), parseFloat(billboard.latitude)]);
          }
        });

        if (packageCoordinates.length > 1) {
          // Fit map to show all billboards
          const bounds = packageCoordinates.reduce((bounds, coord) => {
            return bounds.extend(coord);
          }, new mapboxgl.LngLatBounds());

          map.current?.fitBounds(bounds, {
            padding: { top: 50, bottom: 50, left: 50, right: 320 },
            maxZoom: 15,
            duration: 1000
          });
        } else if (packageCoordinates.length === 1) {
          // Navigate to single billboard location
          map.current?.flyTo({
            center: packageCoordinates[0],
            zoom: Math.max(map.current.getZoom(), 14),
            duration: 1000
          });
        }
      }
      // No popup for package selection - just navigation and marker display
    } else {
      // For regular inventory items
      setSelectedMarkerId(inventoryItem.id);
      // Navigate to the item location
      if (inventoryItem.latitude && inventoryItem.longitude) {
        const lat = parseFloat(inventoryItem.latitude);
        const lng = parseFloat(inventoryItem.longitude);
        map.current?.flyTo({
          center: [lng, lat],
          zoom: Math.max(map.current.getZoom(), 14),
          duration: 1000
        });
      }
    }

    if (onInventorySelect) {
      onInventorySelect(inventoryItem);
    }
  };

  // Initialize Mapbox map
  useEffect(() => {
    if (mapContainer.current && !map.current) {
      map.current = new mapboxgl.Map({
        container: mapContainer.current,
        style: 'mapbox://styles/mapbox/streets-v11',
        center: [101.6869, 3.1390], // Malaysia coordinates as default
        zoom: 6,
        attributionControl: false
      });

      map.current.on('load', () => {
        setMapLoaded(true);

        map.current?.on('click', (e) => {
          const features = map.current?.queryRenderedFeatures(e.point);
          if (!features?.length) {
            setSelectedInventoryItem(null);
            setSelectedMarkerId(null);
            updateMarkerAppearances(null); // Deselect all markers
            // Close all popups when clicking elsewhere on the map
            markersRef.current.forEach(marker => {
              const p = marker.getPopup();
              if (p) {
                p.remove();
                marker.setPopup(null);
              }
            });
          }
        });
      });

      // Add zoom and rotation controls
      map.current.addControl(new mapboxgl.NavigationControl(), 'top-right');

      // Add fullscreen control
      map.current.addControl(new mapboxgl.FullscreenControl(), 'top-right');

      // Add geolocate control
      map.current.addControl(
        new mapboxgl.GeolocateControl({
          positionOptions: {
            enableHighAccuracy: true
          },
          trackUserLocation: true,
          showUserHeading: true
        }),
        'top-right'
      );
    }

    return () => {
      // Cleanup markers
      markersRef.current.forEach(marker => marker.remove());
      markersRef.current = [];

      if (map.current) {
        map.current.remove();
        map.current = null;
      }
      // Clear coord groups
      coordGroupsRef.current.clear();
      coordCycleIndexRef.current.clear();
    };
  }, []);

  // Update map style when mapType changes
  useEffect(() => {
    if (map.current && mapLoaded) {
      const styleUrl = mapType === 'satellite'
        ? 'mapbox://styles/mapbox/satellite-v9'
        : 'mapbox://styles/mapbox/streets-v11';

      map.current.setStyle(styleUrl);
    }
  }, [mapType, mapLoaded]);

  // Create custom map pin marker
  // added optional customColor param for duplicate coloring
  const createMapPinElement = (inventoryItem: any, isSelected: boolean = false, isUserLoggedIn: boolean = false, customColor?: string) => {
    const markerElement = document.createElement('div');
    markerElement.className = 'custom-map-pin';
    markerElement.style.cursor = 'pointer';
    markerElement.style.width = '20px';
    markerElement.style.height = '20px';
    // Keep marker z-index low so popups always render on top
    markerElement.style.zIndex = isSelected ? '40' : '30';
    markerElement.style.position = 'absolute';
    markerElement.style.pointerEvents = 'auto';

    // decide color - allow caller to override for duplicate coloring
    let pinColor = customColor || undefined;

    // fallback/override rules when no customColor supplied
    if (!pinColor) {
      if (isUserLoggedIn && !inventoryItem.availableBooking) {
        pinColor = '#ef4444';
      } else if (inventoryItem.discountedPrice || inventoryItem.discountPercentage) {
        pinColor = '#f59e0b';
      } else {
        pinColor = '#22c55e';
      }
    }

    markerElement.style.backgroundColor = pinColor;
    markerElement.style.border = `2px solid ${isSelected ? '#3b82f6' : '#ffffff'}`;
    markerElement.style.borderRadius = '50%';

    // store custom color only if explicitly provided so updateMarkerAppearances can respect actual state
    if (customColor) {
      markerElement.setAttribute('data-custom-color', customColor);
    } else {
      // make sure we don't override sold-out or computed color later
      markerElement.removeAttribute('data-custom-color');
    }

    if (isSelected) {
      const innerCircle = document.createElement('div');
      innerCircle.className = 'selection-inner';
      innerCircle.style.position = 'absolute';
      innerCircle.style.top = '50%';
      innerCircle.style.left = '50%';
      innerCircle.style.transform = 'translate(-50%, -50%)';
      innerCircle.style.width = '10px';
      innerCircle.style.height = '10px';
      innerCircle.style.borderRadius = '50%';
      innerCircle.style.backgroundColor = pinColor;
      // make selection circle more subtle so it won't visually compete with popup
      innerCircle.style.opacity = '0.28';
      markerElement.appendChild(innerCircle);
    }

    return markerElement;
  };

  // Create popup content as HTML string
  const createPopupContent = (inventoryItem: any, packageItem?: any) => {
    // Check if this is a package item - using the actual package data structure
    const isPackage = packageItem || (inventoryItem.inventory || inventoryItem.inventories || inventoryItem.registryPackageType || inventoryItem.totalPrice !== undefined);

    // Determine cart state for edit and normal flow
    let isItemInCart = false;

    // For packages, use the isInCart flag from API response (set by start-campaign component)
    if (isPackage && packageItem) {
      isItemInCart = packageItem.isInCart || packageItem._isInCart || false;
      console.log(`[POPUP CONTENT] Package ${packageItem.id}:`, {
        name: packageItem.name,
        isInCart: packageItem.isInCart,
        _isInCart: packageItem._isInCart,
        finalIsItemInCart: isItemInCart
      });
    } else if (isPackage && !packageItem) {
      isItemInCart = inventoryItem.isInCart || inventoryItem._isInCart || false;
      console.log(`[POPUP CONTENT] Package ${inventoryItem.id}:`, {
        name: inventoryItem.name,
        isInCart: inventoryItem.isInCart,
        _isInCart: inventoryItem._isInCart,
        finalIsItemInCart: isItemInCart
      });
    } else {
      // For regular inventory, use existing logic
      // Check for optimistic update first
      if (inventoryItem._isInCart !== undefined) {
        isItemInCart = inventoryItem._isInCart;
      }
      // Then check normal cart state
      else if (isInCart) {
        isItemInCart = isInCart(inventoryItem.id);
      }
      // Fallback to other checks
      else if (edit) {
        isItemInCart = draftData?.campaignInventories?.some((inv: any) => inv.inventoryId === inventoryItem.id);
      } else if (cartDataList) {
        isItemInCart = cartDataList.some((cart: any) =>
          cart.campaignInventories?.some((inv: any) => inv.inventoryId === inventoryItem.id)
        );
      }
    }

    // helper to choose best image url for an item
    const getValidImage = (item: any) => {
      if (!item) return null;
      const full = item.fullResImagePath;
      if (full && full !== "null") return full;
      if (item.thumbnail) return item.thumbnail;
      if (item.imageUrl) return item.imageUrl;
      return null;
    };

    if (isPackage) {
      // If packageItem is provided, we're showing a billboard from a package
      const displayItem = packageItem || inventoryItem;
      const billboardItem = packageItem ? inventoryItem : null;

      // Check package availability based on campaign dates
      const packageAvailable = isPackageAvailable(displayItem);

      // compute image src: prefer billboard image, then package image, then nomedia
      const billboardImg = getValidImage(billboardItem);
      const packageImg = getValidImage(displayItem);
      const imgSrc = billboardImg || packageImg || `${import.meta.env.VITE_APP_FOLDER_PATH}/nomedia.jpg`;

      // Package popup content - show billboard name but package details
      return `
        <div style="width: 280px; padding: 0; font-family: system-ui, -apple-system, sans-serif;">
          <div style="position: relative; margin-bottom: 12px;">
            <img 
              src="${imgSrc}" 
              alt="${billboardItem?.displayName || billboardItem?.name || displayItem.name || 'Package'}"
              style="width: 100%; height: 100px; object-fit: cover; border-radius: 8px 8px 0 0;"
            />
          </div>
          
          <div style="padding: 12px;">
            <h3 style="font-size: 16px; font-weight: 600; color: #111827; margin: 0 0 8px 0; line-height: 1.4;">
              ${billboardItem ? (billboardItem.displayName || billboardItem.name || 'Billboard') : (displayItem.name || 'Package Deal')}
            </h3>
            ${billboardItem ? `
            <h3 style="font-size: 14px; font-weight: 600; color: #111827; margin: 0 0 8px 0; line-height: 1.4;">
              package Name: ${displayItem.name}
            </h3>
            ` : ''}
            
            <p style="font-size: 12px; color: #6b7280; margin: 0 0 8px 0;">
              ${(() => {
          const d = billboardItem ? (billboardItem.trDistrictName || '') : (displayItem.country?.name || displayItem.trDistrictName || '');
          const s = billboardItem ? (billboardItem.trStateName || '') : (displayItem.state || displayItem.trStateName || '');
          return [d, s].filter(Boolean).join(', ');
        })()}
            </p>
            
            
            <p style="font-size: 11px; color: #059669; font-weight: 500; margin: 0 0 12px 0;">
              ${billboardItem ? `by ${billboardItem.mediaOwnerName || 'Multiple Media Owner'}` : `${displayItem.publisher?.name || displayItem.mediaOwnerName || 'Publisher'}`}
            </p>
            
            <div style="margin-bottom: 12px;">
            <p style="font-size: 11px; color: #9ca3af ; font-style: italic;">
              Package Price:
            </p>
              <span style="font-size: 18px; font-weight: 700; color: #059669;">
                ${(() => {
          const price = displayItem.totalPrice || displayItem.price || displayItem.currentPrice;
          if (!price || price === 0) return 'N/A';
            return `${displayItem?.currency || displayItem?.price?.localCurrencies?.code || currencyCode || "N/A"} ${Number(price).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
           })()}
              </span>
            </div>

          

            <div style="display: flex; gap: 8px;">
              <button 
                onclick="window.handlePopupViewDetails('${displayItem.id}')"
                style="flex: 1; padding: 8px 12px; background: white; border: 1px solid #d1d5db; border-radius: 6px; font-size: 12px; font-weight: 500; color: #374151; cursor: pointer; transition: all 0.2s;"
                onmouseover="this.style.backgroundColor='#f9fafb'; this.style.borderColor='#9ca3af';"
                onmouseout="this.style.backgroundColor='white'; this.style.borderColor='#d1d5db';"
              >
                Details
              </button>
              
            </div>
          </div>
        </div>
      `;
    }

    // Regular inventory popup content
    const inventoryImg = getValidImage(inventoryItem) || `${import.meta.env.VITE_APP_FOLDER_PATH}/nomedia.jpg`;
    return `
      <div style="width: 300px; padding: 0; font-family: system-ui, -apple-system, sans-serif;">
        <div style="position: relative; margin-bottom: 12px;">
          <img 
            src="${inventoryImg}" 
            alt="${inventoryItem.displayName || inventoryItem.name || 'Billboard'}"
            style="width: 100%; height: 120px; object-fit: cover; border-radius: 8px 8px 0 0;"
          />
          ${isLoggedIn ? `
          <div style="position: absolute; top: 8px; right: 8px;">
            <span style="background-color: ${(!inventoryItem.availableBooking || !inventoryItem.active) ? '#ef4444' : '#22c55e'}; color: white; padding: 4px 8px; border-radius: 4px; font-size: 11px; font-weight: 500;">
              ${(!inventoryItem.availableBooking || !inventoryItem.active) ? 'Sold Out' : 'Available'}
            </span>
          </div>
          ` : ''}
          ${(inventoryItem.discountedPrice || inventoryItem.discountPercentage) ?
        `<div style="position: absolute; top: 8px; left: 8px;">
              <span style="background-color: #ef4444; color: white; padding: 4px 8px; border-radius: 4px; font-size: 11px; font-weight: 500;">
                ${inventoryItem.discountPercentage || '10'}% OFF
              </span>
            </div>` : ''
      }
        </div>
        
        <div style="padding: 12px;">
          <h3 style="font-size: 16px; font-weight: 600; color: #111827; margin: 0 0 8px 0; line-height: 1.4;">
            ${inventoryItem.displayName || inventoryItem.name || 'Billboard'}
          </h3>
          
          <p style="font-size: 12px; color: #6b7280; margin: 0 0 8px 0;">
            ${`${inventoryItem.trDistrictName || ''}, ${inventoryItem.trStateName || ''}`}
          </p>
          
          <p style="font-size: 11px; color: #059669; font-weight: 500; margin: 0 0 12px 0;">
            by ${inventoryItem.mediaOwnerName || 'N/A'}
          </p>
          
          <div style="margin-bottom: 12px;">
            ${(() => {
        let price;
        let suffix = '';

        if (inventoryItem.discountedPrice && inventoryItem.discountedPrice > 0) {
          price = inventoryItem.discountedPrice;
          suffix = isLoggedIn
            ? (inventoryItem.type === 'DIGITAL'
              ? (priceFormat === 'spot-based' ? '/Spot' : '/Day')
              : '/Day')
            : '/Month';

          if (!price || price === 0) return '<span style="font-size: 18px; font-weight: 700; color: #059669;">N/A</span>';

          return `<div style="display: flex; align-items: center; gap: 8px;">
                <span style="font-size: 18px; font-weight: 700; color: #ef4444;">
                 ${inventoryItem?.price?.localCurrencies?.code || currencyCode || "N/A"} ${Number(price).toLocaleString('en-US', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
          })}${suffix}
                </span>
                <span style="font-size: 12px; color: #9ca3af; text-decoration: line-through;">
                  ${inventoryItem?.price?.localCurrencies?.code || currencyCode || "N/A"} ${Number(inventoryItem.currentPrice || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </span>
              </div>`;
        } else {
          if (inventoryItem.type === 'DIGITAL') {
            price = priceFormat === 'spot-based'
              ? inventoryItem.perSpotPrice
              : inventoryItem.perDayPrice;
            suffix = priceFormat === 'spot-based' ? '/Spot' : '/Day';
          } else {
            price = (inventoryItem.sellingRate?.monthly?.month1 && inventoryItem.sellingRate.monthly.month1 > 0)
              ? inventoryItem.sellingRate.monthly.month1
              : inventoryItem.currentPrice;
            suffix = '/Month';
          }

          if (!price || price === 0) return '<span style="font-size: 18px; font-weight: 700; color: #059669;">N/A</span>';

          return `<div>
                <span style="font-size: 18px; font-weight: 700; color: #059669;">
                  ${inventoryItem?.price?.localCurrencies?.code || currencyCode || "N/A"} ${Number(price).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}${suffix}
                </span>
              </div>`;
        }
      })()}
          </div>

          ${isLoggedIn ? `
          <div style="display: flex; align-items: center; justify-content: space-between; font-size: 11px; color: #6b7280; margin-bottom: 12px;">
            <span>
              ${inventoryItem.type === 'CLASSIC' ?
        (inventoryItem.panelWidth && inventoryItem.panelHeight ? `${inventoryItem.panelWidth}x${inventoryItem.panelHeight}` : 'Standard') :
        (inventoryItem.resolutionWidth && inventoryItem.resolutionHeight ? `${inventoryItem.resolutionWidth}x${inventoryItem.resolutionHeight}` : 'Standard')
      }
            </span>
            <span>
              ${inventoryItem.monthlySummary?.totalVisitors ? Number(inventoryItem.monthlySummary.totalVisitors)?.toLocaleString() : 'N/A'} impressions
            </span>
          </div>
          ` : `
          <div style="background-color: #f3f4f6; border: 1px solid #e5e7eb; border-radius: 6px; padding: 12px; margin-bottom: 12px; display: flex; align-items: center; justify-content: center;">
            <svg style="width: 14px; height: 14px; margin-right: 8px; color: #6b7280;" fill="currentColor" viewBox="0 0 20 20"><path fill-rule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clip-rule="evenodd" /></svg>
            <span style="font-size: 11px; color: #6b7280;">Login to view full audience and performance data</span>
          </div>
          `}


          <div style="display: flex; gap: 8px;">
            <button 
              onclick="window.handlePopupViewDetails('${inventoryItem.id}')"
              style="flex: 1; padding: 8px 12px; background: white; border: 1px solid #d1d5db; border-radius: 6px; font-size: 12px; font-weight: 500; color: #374151; cursor: pointer; transition: all 0.2s;"
              onmouseover="this.style.backgroundColor='#f9fafb'; this.style.borderColor='#9ca3af';"
              onmouseout="this.style.backgroundColor='white'; this.style.borderColor='#d1d5db';"
            >
              Details
            </button>
            ${!loadingCartItemIds?.has(inventoryItem.id) ? `
            <button 
              onclick="window.handlePopupCartAction('${inventoryItem.id}')"
              style="
                flex: 1;
                padding: 8px 12px;
                background: ${(() => {
          const isPriceZero = !inventoryItem.currentPrice || inventoryItem.currentPrice === 0;
          if (isPriceZero) return '#9ca3af';
          if (isLoggedIn && (!inventoryItem.availableBooking || !inventoryItem.active)) return '#9ca3af';
          return isItemInCart ? '#ef4444' : '#059669';
        })()};
                border: none;
                border-radius: 6px;
                font-size: 12px;
                font-weight: 500;
                color: white;
                cursor: ${(() => {
          const isPriceZero = !inventoryItem.currentPrice || inventoryItem.currentPrice === 0;
          if (isPriceZero) return 'not-allowed';
          if (isLoggedIn && (!inventoryItem.availableBooking || !inventoryItem.active)) return 'not-allowed';
          return 'pointer';
        })()};
                transition: all 0.2s;
              "
              ${(() => {
          const isPriceZero = !inventoryItem.currentPrice || inventoryItem.currentPrice === 0;
          if (isPriceZero) return 'disabled';
          if (isLoggedIn && (!inventoryItem.availableBooking || !inventoryItem.active)) return 'disabled';
          return '';
        })()}
              onmouseover="if(!this.disabled) this.style.backgroundColor='${(() => {
          const isPriceZero = !inventoryItem.currentPrice || inventoryItem.currentPrice === 0;
          if (isPriceZero) return '#9ca3af';
          if (isLoggedIn && (!inventoryItem.availableBooking || !inventoryItem.active)) return '#9ca3af';
          return isItemInCart ? '#dc2626' : '#047857';
        })()}';"
              onmouseout="if(!this.disabled) this.style.backgroundColor='${(() => {
          const isPriceZero = !inventoryItem.currentPrice || inventoryItem.currentPrice === 0;
          if (isPriceZero) return '#9ca3af';
          if (isLoggedIn && (!inventoryItem.availableBooking || !inventoryItem.active)) return '#9ca3af';
          return isItemInCart ? '#ef4444' : '#059669';
        })()}';"
            >
              ${(() => {
          const isPriceZero = !inventoryItem.currentPrice || inventoryItem.currentPrice === 0;
          if (isPriceZero) return 'Unavailable';
          if (isLoggedIn && (!inventoryItem.availableBooking || !inventoryItem.active)) return 'Sold Out';
          return isItemInCart ? 'Remove' : 'Add to Cart';
        })()}
            </button>
            ` : `
              <button 
                style="flex: 1; padding: 8px 12px; background: #9ca3af; border: none; border-radius: 6px; font-size: 12px; font-weight: 500; color: white; cursor: not-allowed;"
                disabled
              >
                <div style="display: inline-block; margin-right: 8px; width: 16px; height: 16px; border: 2px solid white; border-bottom-color: transparent; border-radius: 50%; animation: spin 1s linear infinite;"></div>
                Processing...
              </button>
            `}
          </div>
        </div>
      </div>
    `;
  };

  // Reset package selection when inventory changes
  useEffect(() => {
    setSelectedPackageId(null);
  }, [inventory]);

  // Add markers for inventory items
  useEffect(() => {
    if (map.current && mapLoaded && inventory.length > 0) {
      // Clear existing markers and coord groups
      markersRef.current.forEach(marker => marker.remove());
      markersRef.current = [];
      coordGroupsRef.current.clear();
      coordCycleIndexRef.current.clear();

      // Determine which package to show (selected or first package)
      let targetPackage = null;
      if (selectedPackageId) {
        targetPackage = inventory.find(item => item.id === selectedPackageId);
      } else {
        // Show first package by default
        targetPackage = inventory.find(item =>
          item.inventory || item.inventories || item.registryPackageType || item.totalPrice !== undefined
        );
        if (targetPackage) {
          setSelectedPackageId(targetPackage.id);
        }
      }

      // Add new markers
      inventory.forEach((inventoryItem) => {
        const isPackage = inventoryItem.inventory || inventoryItem.inventories || inventoryItem.registryPackageType || inventoryItem.totalPrice !== undefined;

        if (isPackage && inventoryItem.inventories && inventoryItem.inventories.length > 0) {
          // Only show markers for the selected package
          if (inventoryItem.id === selectedPackageId) {
            // For selected package, show ALL billboard locations
            inventoryItem.inventories.forEach((billboard: any) => {
              if (billboard.latitude && billboard.longitude &&
                !isNaN(parseFloat(billboard.latitude)) && !isNaN(parseFloat(billboard.longitude))) {

                const lat = parseFloat(billboard.latitude);
                const lng = parseFloat(billboard.longitude);
                console.log(`[PACKAGE MARKER] Adding marker for package ${inventoryItem.name || inventoryItem.id} | Billboard: ${billboard.displayName || billboard.name || billboard.id} | Location: ${lat}, ${lng}`);

                // compute key for grouping duplicates (fixed precision)
                const key = `${lng.toFixed(6)}_${lat.toFixed(6)}`;
                const group = coordGroupsRef.current.get(key) || [];

                // Do NOT force duplicate markers to pink — keep sold-out billboards red.
                // Pass billboard as the item to derive availability/color.
                // Set package markers to green pointer color
                const packageGreen = '#22c55e';
                const markerElement = createMapPinElement(
                  billboard, // use billboard so its availableBooking/active are respected
                  false, // No selection state for individual billboards
                  isLoggedIn,
                  packageGreen // custom override to show package markers in green
                );

                // Create and add marker
                const marker = new mapboxgl.Marker({
                  element: markerElement,
                  anchor: 'center',
                })
                  .setLngLat([lng, lat])
                  .addTo(map.current!);

                // Add data attributes to track both package and billboard and coord key
                markerElement.setAttribute('data-inventory-id', inventoryItem.id.toString());
                markerElement.setAttribute('data-billboard-id', billboard.id ? billboard.id.toString() : '');
                markerElement.setAttribute('data-coord-key', key);

                // Add to coord group
                const entry = { marker, inventoryItem, billboard, isPackage: true };
                if (!coordGroupsRef.current.has(key)) coordGroupsRef.current.set(key, []);
                coordGroupsRef.current.get(key)!.push(entry);

                // Update tooltip / badge for all markers in this coord group
                updateGroupLabels(key);

                // Unified click handler (cycles through group when duplicates exist)
                markerElement.addEventListener('click', (e) => {
                  e.stopPropagation();
                  const keyAttr = markerElement.getAttribute('data-coord-key')!;
                  const groupArr = coordGroupsRef.current.get(keyAttr) || [];
                  if (groupArr.length === 0) return;

                  // Close all other popups
                  markersRef.current.forEach(m => {
                    const existingPopup = m.getPopup();
                    if (existingPopup) {
                      existingPopup.remove();
                      m.setPopup(null);
                    }
                  });

                  // If only one marker at this coordinate, open its popup
                  if (groupArr.length === 1) {
                    const only = groupArr[0];
                    const popup = new mapboxgl.Popup({
                      offset: [0, -10],
                      closeButton: true,
                      closeOnClick: false,
                      className: 'billboard-popup',
                      maxWidth: '320px'
                    }).setHTML(createPopupContent(only.billboard, only.inventoryItem));
                    only.marker.setPopup(popup);
                    only.marker.togglePopup();
                    setSelectedInventoryItem(only.inventoryItem);
                    // update labels
                    updateGroupLabels(keyAttr, 1);
                    return;
                  }

                  // Cycle index
                  const prevIdx = coordCycleIndexRef.current.get(keyAttr) || 0;
                  const idx = prevIdx % groupArr.length;
                  const selected = groupArr[idx];

                  // advance index for next click
                  coordCycleIndexRef.current.set(keyAttr, (prevIdx + 1) % groupArr.length);

                  // Show popup for selected entry
                  const popup = new mapboxgl.Popup({
                    offset: [0, -10],
                    closeButton: true,
                    closeOnClick: false,
                    className: 'billboard-popup',
                    maxWidth: '320px'
                  }).setHTML(createPopupContent(selected.billboard, selected.inventoryItem));
                  selected.marker.setPopup(popup);
                  selected.marker.togglePopup();

                  // maintain selection state
                  setSelectedInventoryItem(selected.inventoryItem);

                  // update labels to show current index (idx is zero-based; display idx+1)
                  updateGroupLabels(keyAttr, idx + 1);
                });

                markersRef.current.push(marker);
              }
            });
          }
        } else if (inventoryItem.latitude && inventoryItem.longitude &&
          !isNaN(parseFloat(inventoryItem.latitude)) && !isNaN(parseFloat(inventoryItem.longitude))) {
          // For regular inventory items (if any)
          const lat = parseFloat(inventoryItem.latitude);
          const lng = parseFloat(inventoryItem.longitude);

          // compute key for grouping duplicates (fixed precision)
          const key = `${lng.toFixed(6)}_${lat.toFixed(6)}`;
          const group = coordGroupsRef.current.get(key) || [];

          // Do NOT force duplicate markers to pink — keep sold-out markers red.
          const markerElement = createMapPinElement(
            inventoryItem,
            selectedMarkerId === inventoryItem.id,
            isLoggedIn,
            undefined // let createMapPinElement decide based on availableBooking/discount etc.
          );

          // Create and add marker with anchor set to center (do NOT attach popup here)
          const marker = new mapboxgl.Marker({
            element: markerElement,
            anchor: 'center',
          })
            .setLngLat([lng, lat])
            .addTo(map.current!);

          // Add data attribute to track inventory id and coord key
          markerElement.setAttribute('data-inventory-id', inventoryItem.id.toString());
          markerElement.setAttribute('data-coord-key', key);

          // Add to coord group
          const entry = { marker, inventoryItem, isPackage: false };
          if (!coordGroupsRef.current.has(key)) coordGroupsRef.current.set(key, []);
          coordGroupsRef.current.get(key)!.push(entry);

          // Update tooltip / badge for all markers in this coord group
          updateGroupLabels(key);

          // Unified click handler for groups (cycles through multiple items at same coord)
          markerElement.addEventListener('click', (e) => {
            e.stopPropagation();
            const keyAttr = markerElement.getAttribute('data-coord-key')!;
            const groupArr = coordGroupsRef.current.get(keyAttr) || [];
            if (groupArr.length === 0) return;

            // Close all other popups
            markersRef.current.forEach(m => {
              const existingPopup = m.getPopup();
              if (existingPopup) {
                existingPopup.remove();
                m.setPopup(null);
              }
            });

            if (groupArr.length === 1) {
              const only = groupArr[0];
              const popup = new mapboxgl.Popup({
                offset: [0, -10],
                closeButton: true,
                closeOnClick: false,
                className: 'billboard-popup',
                maxWidth: '320px'
              }).setHTML(createPopupContent(only.inventoryItem));
              only.marker.setPopup(popup);
              only.marker.togglePopup();
              setSelectedInventoryItem(only.inventoryItem);
              setSelectedMarkerId(only.inventoryItem.id);
              // update labels
              updateGroupLabels(keyAttr, 1);
              return;
            }

            // Cycle index for this coordinate
            const prevIdx = coordCycleIndexRef.current.get(keyAttr) || 0;
            const idx = prevIdx % groupArr.length;
            const selected = groupArr[idx];

            // advance index
            coordCycleIndexRef.current.set(keyAttr, (prevIdx + 1) % groupArr.length);

            // Show popup for selected entry
            const popup = new mapboxgl.Popup({
              offset: [0, -10],
              closeButton: true,
              closeOnClick: false,
              className: 'billboard-popup',
              maxWidth: '320px'
            }).setHTML(createPopupContent(selected.inventoryItem));
            selected.marker.setPopup(popup);
            selected.marker.togglePopup();

            setSelectedInventoryItem(selected.inventoryItem);
            setSelectedMarkerId(selected.inventoryItem.id);
            map.current?.flyTo({
              center: [lng, lat],
              zoom: Math.max(map.current.getZoom(), 14),
              duration: 1000
            });

            // update labels to show current index
            updateGroupLabels(keyAttr, idx + 1);
            updateMarkerAppearances(selected.inventoryItem.id);
          });

          markersRef.current.push(marker);
        }
      });

      // Fit map to show all markers if there are multiple valid locations
      const allCoordinates: [number, number][] = [];

      inventory.forEach(item => {
        const isPackage = item.inventory || item.inventories || item.registryPackageType || item.totalPrice !== undefined;

        if (isPackage && item.inventories && item.inventories.length > 0 && item.id === selectedPackageId) {
          // For the selected package, add all billboard coordinates
          item.inventories.forEach((billboard: any) => {
            if (billboard.latitude && billboard.longitude &&
              !isNaN(parseFloat(billboard.latitude)) && !isNaN(parseFloat(billboard.longitude))) {
              allCoordinates.push([parseFloat(billboard.longitude), parseFloat(billboard.latitude)]);
            }
          });
        } else if (!isPackage && item.latitude && item.longitude &&
          !isNaN(parseFloat(item.latitude)) && !isNaN(parseFloat(item.longitude))) {
          // For regular inventory
          allCoordinates.push([parseFloat(item.longitude), parseFloat(item.latitude)]);
        }
      });

      if (allCoordinates.length > 1) {
        const bounds = allCoordinates.reduce((bounds, coord) => {
          return bounds.extend(coord);
        }, new mapboxgl.LngLatBounds());

        map.current?.fitBounds(bounds, {
          padding: { top: 50, bottom: 50, left: 50, right: 320 },
          maxZoom: 15,
          duration: 1000
        });
      } else if (allCoordinates.length === 1) {
        map.current?.flyTo({
          center: allCoordinates[0],
          zoom: 12,
          duration: 1000
        });
      }

    }
  }, [inventory, mapLoaded, selectedMarkerId, currencyCode, selectedPackageId]);

  // Update popup content when cart state or trigger changes
  useEffect(() => {
    markersRef.current.forEach(marker => {
      const popup = marker.getPopup();
      if (popup && popup.isOpen()) {
        const pos = marker.getLngLat();

        // Find the inventory item that matches this marker position
        let inventoryItem = null;
        let billboardItem = null;

        // Check packages first - look for billboards within packages
        for (const item of inventory) {
          const isPackage = item.inventory || item.inventories || item.registryPackageType || item.totalPrice !== undefined;

          if (isPackage && item.inventories && item.inventories.length > 0 && item.id === selectedPackageId) {
            // Find the specific billboard at this position
            const billboard = item.inventories.find((billboard: any) => {
              if (!billboard.latitude || !billboard.longitude) return false;
              return Math.abs(parseFloat(billboard.longitude) - pos.lng) < 0.0001 &&
                Math.abs(parseFloat(billboard.latitude) - pos.lat) < 0.0001;
            });

            if (billboard) {
              inventoryItem = item; // Package item
              billboardItem = billboard; // Specific billboard
              break;
            }
          } else if (!isPackage) {
            // For regular inventory
            if (!item.latitude || !item.longitude) continue;
            if (Math.abs(parseFloat(item.longitude) - pos.lng) < 0.0001 &&
              Math.abs(parseFloat(item.latitude) - pos.lat) < 0.0001) {
              inventoryItem = item;
              break;
            }
          }
        }

        if (inventoryItem) {
          // Remove existing popup and create new one with latest state
          popup.remove();
          const newPopup = new mapboxgl.Popup({
            offset: [0, -10],
            closeButton: true,
            closeOnClick: false,
            className: 'billboard-popup',
            maxWidth: '320px'
          });

          // Use billboard-specific content if available
          if (billboardItem) {
            newPopup.setHTML(createPopupContent(billboardItem, inventoryItem));
          } else {
            newPopup.setHTML(createPopupContent(inventoryItem));
          }

          newPopup.setLngLat(pos);
          newPopup.addTo(map.current!);
          // Attach to marker so subsequent updates are consistent
          marker.setPopup(newPopup);
        }
      }
    });
  }, [isInCart, inventory, currencyCode, loadingCartItemIds, cartDataList, edit, draftData, cartActionTrigger, selectedPackageId]);

  // Update marker appearances when selection changes
  const updateMarkerAppearances = (selectedId: string | null) => {
    if (!markersRef.current.length || !inventory.length) return;

    markersRef.current.forEach((marker, index) => {
      const markerPos = marker.getLngLat();

      // Find the inventory item that matches this marker position
      const inventoryItem = inventory.find(item => {
        const isPackage = item.inventory || item.inventories || item.registryPackageType || item.totalPrice !== undefined;

        if (isPackage && item.inventories && item.inventories.length > 0) {
          // For packages, check if the first billboard matches this position
          const firstBillboard = item.inventories[0];
          if (!firstBillboard.latitude || !firstBillboard.longitude) return false;
          return Math.abs(parseFloat(firstBillboard.longitude) - markerPos.lng) < 0.0001 &&
            Math.abs(parseFloat(firstBillboard.latitude) - markerPos.lat) < 0.0001;
        } else {
          // For regular inventory
          if (!item.latitude || !item.longitude) return false;
          return Math.abs(parseFloat(item.longitude) - markerPos.lng) < 0.0001 &&
            Math.abs(parseFloat(item.latitude) - markerPos.lat) < 0.0001;
        }
      });

      if (!inventoryItem) {
        // Not always fatal — some markers correspond to package billboards
        // We'll skip color changes if we cannot match by top-level heuristics
        return;
      }

      const isSelected = inventoryItem.id === selectedId;
      const element = marker.getElement() as HTMLElement;

      // Respect custom color assigned for duplicates if present; otherwise compute based on item state.
      const customColor = element.getAttribute('data-custom-color');
      let pinColor = customColor || undefined;
      if (!pinColor) {
        if (isLoggedIn && !inventoryItem.availableBooking) {
          pinColor = '#ef4444';
        } else if (inventoryItem.discountedPrice || inventoryItem.discountPercentage) {
          pinColor = '#f59e0b';
        } else {
          pinColor = '#22c55e';
        }
      }

      element.style.backgroundColor = pinColor;
      element.style.border = `2px solid ${isSelected ? '#3b82f6' : '#ffffff'}`;
      // keep z-index low so popups always sit above markers
      element.style.zIndex = isSelected ? '40' : '30';

      const existingInnerCircle = element.querySelector('.selection-inner') as HTMLElement | null;
      if (isSelected && !existingInnerCircle) {
        const innerCircle = document.createElement('div');
        innerCircle.className = 'selection-inner';
        innerCircle.style.position = 'absolute';
        innerCircle.style.top = '50%';
        innerCircle.style.left = '50%';
        innerCircle.style.transform = 'translate(-50%, -50%)';
        innerCircle.style.width = '10px';
        innerCircle.style.height = '10px';
        innerCircle.style.borderRadius = '50%';
        innerCircle.style.backgroundColor = pinColor;
        innerCircle.style.opacity = '0.28';
        element.appendChild(innerCircle);
      } else if (!isSelected && existingInnerCircle) {
        existingInnerCircle.remove();
      }
    });
  };

  // Handle cart actions (both for list and popup)
  const handleCartAction = async (inventoryItem: any) => {
    // Check if this is a package
    const isPackage = !!(inventoryItem.inventory || inventoryItem.inventories || inventoryItem.registryPackageType);

    if (isPackage) {
      // Check package availability based on campaign dates
      if (campaignStartDate && campaignEndDate) {
        const packageAvailable = isPackageAvailable(inventoryItem);
        if (!packageAvailable) return;
      }

      // Check if package is currently in cart
      const isCurrentlyInCart = inventoryItem.isInCart || inventoryItem._isInCart || false;

      console.log(`[HANDLE CART ACTION] Package ${inventoryItem.id}:`, {
        name: inventoryItem.name,
        isInCart: inventoryItem.isInCart,
        _isInCart: inventoryItem._isInCart,
        isCurrentlyInCart: isCurrentlyInCart,
        action: isCurrentlyInCart ? 'REMOVE' : 'ADD'
      });

      // For explore flow, use local cart context
      const isExploreFlow = !localStorage.getItem('authToken');
      if (isExploreFlow) {
        if (isCurrentlyInCart) {
          if (onDeleteFromCart) {
            onDeleteFromCart(inventoryItem);
          }
        } else {
          addToCart({
            id: inventoryItem.id,
            name: inventoryItem.name,
            location: inventoryItem.country?.name || '',
            state: '',
            price: (inventoryItem.totalPrice || 0) * 100, // Convert to cents for cart context
            impressions: inventoryItem.estimation?.potentialViews || 0,
            uniqueVisitors: inventoryItem.monthlySummary?.uniqueVisitors || 0,
            frequency: inventoryItem.monthlySummary?.frequency || 0,
            size: (inventoryItem.inventory?.total || '').toString(),
            type: 'Package',
            status: (inventoryItem.numberOfPurchase < inventoryItem.purchaseLimitation) ? 'available' : 'sold-out',
            image: inventoryItem.thumbnail || `${import.meta.env.VITE_APP_FOLDER_PATH}/nomedia.jpg`,
            spots: undefined,
            campaignDuration: inventoryItem.campaignDuration || undefined,
            dailyHours: undefined,
            isDeal: false,
            daypartSchedules: undefined,
            packageStartDate: inventoryItem.startDate,
            packageEndDate: inventoryItem.endDate,
            requiresCampaignDuration: false,
            packageDetails: inventoryItem,
            currencyCode: inventoryItem?.price?.localCurrencies?.code || 'N/A',
            specifications: {
              resolution: 'Various',
              duration: `${inventoryItem.startDate} - ${inventoryItem.endDate}`
            },
            priceFormat: priceFormat
          });
        }
      } else {
        // Handle package cart action based on current state for authenticated users
        if (isCurrentlyInCart) {
          await handlePackageRemoveFromCart(inventoryItem);
        } else {
          await handlePackageAddToCart(inventoryItem);
        }
      }
    } else {
      // Handle regular inventory
      // Only check availableBooking for logged-in users
      if (isLoggedIn && !inventoryItem.availableBooking) return;

      if (!onAddToCart || !onDeleteFromCart) {
        return;
      }

      const isItemInCart = isInCart && isInCart(inventoryItem.id);
      if (isItemInCart) {
        onDeleteFromCart(inventoryItem);
      } else {
        onAddToCart(inventoryItem);
      }
    }

    // Trigger a re-render
    setCartActionTrigger(prev => prev + 1);
  };

  // Add global functions for popup buttons
  useEffect(() => {
    (window as any).handlePopupViewDetails = (inventoryId: string) => {
      const inventoryItem = inventory.find(item => item.id.toString() === inventoryId);
      if (inventoryItem) {
        // Check if this is a package item
        const isPackage = inventoryItem.inventory || inventoryItem.inventories || inventoryItem.registryPackageType || inventoryItem.totalPrice !== undefined;

        if (isPackage) {
          // For packages, navigate to the first billboard's detail page to avoid multiple navigations
          const billboardType = inventoryItem.registryPackageType || inventoryItem.inventoryType || inventoryItem.type || 'digital';

          // Use campaign dates instead of package validity dates
          const startDate = campaignStartDate ? campaignStartDate.toISOString() : '';
          const endDate = campaignEndDate ? campaignEndDate.toISOString() : '';
          const campaignName = inventoryItem.name || '';
          if (inventoryItem && inventoryItem?.inventories && inventoryItem.inventories.length > 0) {
            const firstBillboard = inventoryItem.inventories[0];
            if (isLoggedIn) {
              navigate(`${base_path}/property-details/${firstBillboard.inventoryId}?type=${encodeURIComponent(firstBillboard?.type)}`, {
                state: {
                  currentPrice: firstBillboard?.sellingRate,
                  startDate: startDate,
                  endDate: endDate,
                  priceFormat: priceFormat,
                  from: 'package',
                  campaignName: campaignName
                }
              });
            } else {
              // Default: navigate to billboard/
              navigate(`${base_path}/billboard/${firstBillboard.inventoryId}?type=${encodeURIComponent(firstBillboard?.type)}`, {
                state: {
                  currentPrice: firstBillboard?.sellingRate,
                  priceFormat: priceFormat,
                  from: 'package',
                  campaignName: campaignName
                }
              });
            }
          }
        } else {
          // For regular inventory, use the existing onInventorySelect handler
          onInventorySelect(inventoryItem);
        }
      } else {
        console.error("MAP_VIEW-------No inventory item found for ID:", inventoryId);
      }
    };

      (window as any).handlePopupCartAction = async (inventoryId: string) => {
        const inventoryItem = inventory.find(item => item.id === inventoryId);
        if (inventoryItem) {
          // Check if this is a package
          const isPackage = !!(inventoryItem.inventory || inventoryItem.inventories || inventoryItem.registryPackageType);

          // Find the marker for this inventory
          // Get coordinates: Use top-level for regular inventory, or first inventory for packages
          let latitude = inventoryItem.latitude;
          let longitude = inventoryItem.longitude;

          if (isPackage && (!latitude || !longitude) && inventoryItem.inventories && inventoryItem.inventories.length > 0) {
            latitude = inventoryItem.inventories[0].latitude;
            longitude = inventoryItem.inventories[0].longitude;
          }

          const marker = markersRef.current.find(m => {
            const pos = m.getLngLat();
            return Math.abs(pos.lng - parseFloat(longitude)) < 0.0001 &&
              Math.abs(pos.lat - parseFloat(latitude)) < 0.0001;
          });

          if (marker) {
            let currentlyInCart = false;

            if (isPackage) {
              // For packages, check the isInCart flag from API response
              currentlyInCart = inventoryItem.isInCart || inventoryItem._isInCart || false;
              console.log(`[PACKAGE CART ACTION] Package ${inventoryItem.id}:`, {
                name: inventoryItem.name,
                isInCart: inventoryItem.isInCart,
                _isInCart: inventoryItem._isInCart,
                currentlyInCart: currentlyInCart
              });
            } else {
              // For regular inventory, use existing logic
              currentlyInCart = (isInCart && isInCart(inventoryItem.id)) || false;
            }

            // Update existing popup content immediately with optimistic state
            const popup = marker.getPopup();
            if (popup && popup.isOpen()) {
              // Create optimistic state - if currently in cart, we'll remove it, if not in cart, we'll add it
              const optimisticInventoryItem = {
                ...inventoryItem,
                _isInCart: !currentlyInCart,
                isInCart: !currentlyInCart
              };
              const optimisticContent = createPopupContent(optimisticInventoryItem);
              popup.setHTML(optimisticContent);
            }

            // Handle cart action based on type and current state
            if (isPackage) {
              // Check if it's explore flow (unauthenticated)
              const isExploreFlow = !localStorage.getItem('authToken');
              if (isExploreFlow) {
                // For explore flow, use local cart context (no API call)
                if (currentlyInCart) {
                  if (onDeleteFromCart) {
                    onDeleteFromCart(inventoryItem);
                  }
                } else {
                  // Add to cart using context
                  const cartItem: CartItem = {
                    id: inventoryItem.id,
                    name: inventoryItem.name,
                    location: inventoryItem.country?.name || '',
                    state: '',
                    price: (inventoryItem.totalPrice || 0) * 100, // Convert to cents for cart context
                    impressions: inventoryItem.estimation?.potentialViews || 0,
                    uniqueVisitors: inventoryItem.monthlySummary?.uniqueVisitors || 0,
                    frequency: inventoryItem.monthlySummary?.frequency || 0,
                    size: (inventoryItem.inventory?.total || '').toString(),
                    type: 'Package',
                    status: (inventoryItem.numberOfPurchase < inventoryItem.purchaseLimitation) ? 'available' : 'sold-out',
                    image: inventoryItem.thumbnail || `${import.meta.env.VITE_APP_FOLDER_PATH}/nomedia.jpg`,
                    spots: undefined,
                    campaignDuration: inventoryItem.campaignDuration || undefined,
                    dailyHours: undefined,
                    isDeal: false,
                    daypartSchedules: undefined,
                    packageStartDate: inventoryItem.startDate,
                    packageEndDate: inventoryItem.endDate,
                    requiresCampaignDuration: false,
                    packageDetails: inventoryItem,
                    currencyCode: currencyCode,
                    priceFormat: priceFormat,
                    specifications: {
                      resolution: 'Various',
                      duration: `${inventoryItem.startDate} - ${inventoryItem.endDate}`
                    }
                  };
                  addToCart(cartItem);
                }
              } else {
                // For authenticated users, use API calls
                console.log(`[PACKAGE CART ACTION] Will ${currentlyInCart ? 'REMOVE' : 'ADD'} package ${inventoryItem.id}`);
                if (currentlyInCart) {
                  await handlePackageRemoveFromCart(inventoryItem);
                } else {
                  await handlePackageAddToCart(inventoryItem);
                }
              }
            } else {
              // For regular inventory, use existing handlers
              if (currentlyInCart) {
                await onDeleteFromCart?.(inventoryItem);
              } else {
                await onAddToCart?.(inventoryItem);
              }
            }

            // Quick update after action completes
            await new Promise(resolve => setTimeout(resolve, 100));

            // Update with confirmed state if popup exists and action was successful
            const updatedPopup = marker.getPopup();
            if (updatedPopup && updatedPopup.isOpen()) {
              // Get the updated inventory item from the array
              const updatedInventoryItem = inventory.find(item => item.id === inventoryItem.id) || inventoryItem;
              const confirmedContent = createPopupContent(updatedInventoryItem);
              updatedPopup.setHTML(confirmedContent);
            }
          }
        } else {
          console.error(`No inventory item found for ID: ${inventoryId}`);
        }
      };

      return () => {
        delete (window as any).handlePopupViewDetails;
        delete (window as any).handlePopupCartAction;
      };
    }, [inventory, onInventorySelect, onAddToCart, onDeleteFromCart, isInCart, selectedMarkerId, selectedPackageId, handlePackageAddToCart]);

  // Add CSS for animations and popup styling
  useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
    .billboard-popup .mapboxgl-popup-content {
      padding: 0;
      border-radius: 8px;
      box-shadow: 0 10px 25px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05);
      border: 1px solid #e5e7eb;
      position: relative;
      z-index: 2147483647;
    }
    
    .billboard-popup .mapboxgl-popup-tip {
      border-top-color: #ffffff;
    }
    
    .billboard-popup .mapboxgl-popup-close-button {
      position: absolute;
      top: 8px;
      right: 8px;
      background: rgba(0,0,0,0.5);
      color: white;
      border-radius: 50%;
      width: 24px;
      height: 24px;
      font-size: 14px;
      display: flex;
      align-items: center;
      justify-content: center;
      z-index: 2147483648;
    }
    
    /* Force popups above markers */
    .mapboxgl-popup, .billboard-popup {
      z-index: 2147483647 !important;
      pointer-events: auto;
    }
    
    .custom-map-pin {
        position: absolute;
        transform: translate(-50%, -50%);
        /* keep marker z-index lower than popups */
        z-index: 30 !important;
      /* small smoothing to prevent visual overlap */
      will-change: transform, opacity;
    }
    
      .custom-map-pin:hover {
      filter: drop-shadow(0 4px 8px rgba(0,0,0,0.4));
    }

    /* small numeric badge used when multiple billboards share the same coordinates */
    .custom-map-pin .duplicate-count {
      pointer-events: none;
      font-family: system-ui, -apple-system, sans-serif;
    }
  `;
    document.head.appendChild(style);
    return () => {
      if (document.head.contains(style)) {
        document.head.removeChild(style);
      }
    };
  }, []);

  const handleInventoryClick = (inventoryItem: any) => {
    const isPackage = inventoryItem.inventory || inventoryItem.inventories || inventoryItem.registryPackageType || inventoryItem.totalPrice !== undefined;

    if (isPackage) {
      // For packages, update selectedPackageId to show all billboard locations
      setSelectedPackageId(inventoryItem.id);
      setSelectedInventoryItem(inventoryItem);
      // Don't set selectedMarkerId for packages to prevent popup on selection
    } else {
      // For regular inventory items, use normal behavior
      setSelectedInventoryItem(inventoryItem);
      setSelectedMarkerId(inventoryItem.id);
    }
  };

  // Handle map mode change
  const handleMapModeChange = (mode: 'map' | 'satellite') => {
    setMapMode(mode);
    setMapType(mode);
  };

  // Open popup after map navigation
  const openPopupForMarker = (inventoryItem: any) => {
    // Check if this is a package
    const isPackage = inventoryItem.inventory || inventoryItem.inventories || inventoryItem.registryPackageType || inventoryItem.totalPrice !== undefined;

    // Get coordinates: Use top-level for regular inventory, or first inventory for packages
    let latitude = inventoryItem.latitude;
    let longitude = inventoryItem.longitude;

    if (isPackage && (!latitude || !longitude) && inventoryItem.inventories && inventoryItem.inventories.length > 0) {
      latitude = inventoryItem.inventories[0].latitude;
      longitude = inventoryItem.inventories[0].longitude;
    }

    if (!latitude || !longitude) {
      console.warn('MAP_VIEW-------No valid coordinates for inventoryItem:', inventoryItem.id, 'isPackage:', isPackage);
      return;
    }

    const marker = markersRef.current.find(m => {
      const pos = m.getLngLat();
      return Math.abs(pos.lng - parseFloat(longitude)) < 0.0001 &&
        Math.abs(pos.lat - parseFloat(latitude)) < 0.0001;
    });
    if (marker) {
      markersRef.current.forEach(m => {
        if (m !== marker && m.getPopup()) {
          m.setPopup(null);
        }
      });
      const popup = new mapboxgl.Popup({
        offset: [0, -10],
        closeButton: true,
        closeOnClick: false,
        className: 'billboard-popup',
        maxWidth: '320px'
      }).setHTML(createPopupContent(inventoryItem));
      marker.setPopup(popup).togglePopup();
    } else {
      console.error('MAP_VIEW-------No marker found for billboard ID:', inventoryItem.id, 'isPackage:', isPackage);
    }
  };
  const shownCount = (() => {
    let count = 0;
    // count regular inventory items with valid coordinates
    inventory.forEach(item => {
      const isPackage = item.inventory || item.inventories || item.registryPackageType || item.totalPrice !== undefined;
      if (!isPackage) {
        if (item.latitude && item.longitude && !isNaN(parseFloat(item.latitude)) && !isNaN(parseFloat(item.longitude))) {
          count++;
        }
      }
    });

    // if a package is selected, count its billboards with valid coordinates
    if (selectedPackageId) {
      const pkg = inventory.find(it => it.id === selectedPackageId);
      if (pkg && Array.isArray(pkg.inventories)) {
        pkg.inventories.forEach((bb: any) => {
          if (bb && bb.latitude && bb.longitude && !isNaN(parseFloat(bb.latitude)) && !isNaN(parseFloat(bb.longitude))) {
            count++;
          }
        });
      }
    }

    return count;
  })();

  return (
    <div className="flex flex-col lg:flex-row h-[600px] md:h-[700px] bg-white rounded-lg overflow-hidden shadow-sm border">
      {/* Left Panel - Map */}
      <div className="flex-1 relative bg-gray-100 overflow-hidden">
        <div
          ref={mapContainer}
          className="absolute inset-0 w-full h-full"
          style={{ minHeight: '400px' }}
        />

        {/* Map Controls */}
        <div className="absolute top-2 right-8 sm:top-4 sm:right-8 flex flex-col space-y-2 z-10">
          <div className="bg-white rounded-lg shadow-lg border mx-3">
            <Button
              variant={mapMode === 'map' ? 'default' : 'ghost'}
              size="sm"
              onClick={() => handleMapModeChange('map')}
              className={`rounded-b-none text-xs sm:text-sm px-3 py-2 ${mapMode === 'map' ? 'bg-emerald-600 hover:bg-emerald-700 text-white' : ''}`}
            >
              Map
            </Button>
            <Button
              variant={mapMode === 'satellite' ? 'default' : 'ghost'}
              size="sm"
              onClick={() => handleMapModeChange('satellite')}
              className={`rounded-t-none text-xs sm:text-sm px-3 py-2 ${mapMode === 'satellite' ? 'bg-emerald-600 hover:bg-emerald-700 text-white' : ''}`}
            >
              Satellite
            </Button>
          </div>
        </div>

        {/* Legend */}
        <div className="absolute bottom-2 left-2 sm:bottom-4 sm:left-4 bg-white rounded-lg shadow-lg border p-3 z-10">
          <h5 className="text-xs font-semibold mb-2 text-gray-900">Legend</h5>
          <div className="space-y-2 text-xs">
            {!inventory.some(item => item.inventory || item.inventories || item.registryPackageType || item.totalPrice !== undefined) && (
              <>
                <div className="flex items-center space-x-2">
                  <div style={{ backgroundColor: '#22c55e', width: '16px', height: '16px', borderRadius: '50%' }}></div>
                  <span className="text-gray-700">Available</span>
                </div>
                <div className="flex items-center space-x-2">
                  <div style={{ backgroundColor: '#ef4444', width: '16px', height: '16px', borderRadius: '50%' }}></div>
                  <span className="text-gray-700">Sold Out</span>
                </div>
                <div className="flex items-center space-x-2">
                  <div style={{ backgroundColor: '#f59e0b', width: '16px', height: '16px', borderRadius: '50%' }}></div>
                  <span className="text-gray-700">Special Deal</span>
                </div>
              </>
            )}
            {inventory.some(item => item.inventory || item.inventories || item.registryPackageType || item.totalPrice !== undefined) && (
              <>
                <div className="flex items-center space-x-2">
                  <div style={{ backgroundColor: '#22c55e', width: '16px', height: '16px', borderRadius: '50%' }}></div>
                  <span className="text-gray-700">Single Inventory</span>
                </div>
                <div className="flex items-center space-x-2">
                  <div style={{ backgroundColor: '#f660c1ff', width: '16px', height: '16px', borderRadius: '50%' }}></div>
                  <span className="text-gray-700">Multiple Inventories</span>
                </div>
              </>
            )}
          </div>
        </div>

        {/* Map Stats */}
        <div className="absolute top-2 left-2 sm:top-4 sm:left-4 bg-white rounded-lg shadow-lg border p-3 z-10">
          <div className="text-xs text-gray-600">
            <div className="font-semibold text-gray-900 mb-1">Map View</div>
            <div>Total: {inventory.length} {inventory.some(item => item.inventory || item.inventories || item.registryPackageType || item.totalPrice !== undefined) ? 'Packages' : 'Inventory'}</div>
            <div>Shown: {shownCount} on map</div>
          </div>
        </div>

        {/* Loading overlay */}
        {!mapLoaded && (
          <div className="absolute inset-0 flex items-center justify-center bg-gray-100 z-30">
            <div className="text-center">
              <div className="animate-spin w-8 h-8 border-4 border-emerald-600 border-t-transparent rounded-full mx-auto mb-4"></div>
              <p className="text-gray-600">Loading map...</p>
            </div>
          </div>
        )}
      </div>

      {/* Right Panel - Inventory List */}
      <div className="w-full lg:w-96 xl:w-[32rem] border-l lg:border-l border-b lg:border-b-0 bg-white overflow-y-auto h-80 lg:h-full">
        <div className="p-3 sm:p-4 border-b bg-gray-50">
          <h3 className="font-semibold text-gray-900 text-sm sm:text-base">
            {inventory.some(item => item.inventory || item.inventories || item.registryPackageType || item.totalPrice !== undefined)
              ? 'Package Locations'
              : 'Inventory Locations'
            }
          </h3>
          <p className="text-xs sm:text-sm text-gray-600">
            {inventory.length} Total • {inventory.filter(item =>
              item.latitude && item.longitude &&
              !isNaN(parseFloat(item.latitude)) && !isNaN(parseFloat(item.longitude))
            ).length} With Coordinates
          </p>
        </div>
        <div className="divide-y">
          {inventory.map((inventoryItem) => {
            const isPackage = inventoryItem.inventory || inventoryItem.inventories || inventoryItem.registryPackageType || inventoryItem.totalPrice !== undefined;

            // For packages, use the isInCart flag from API response, for regular inventory use the isInCart function
            const isItemInCart = isPackage
              ? (inventoryItem.isInCart || inventoryItem._isInCart || false)
              : (isInCart && isInCart(inventoryItem.id));

            const packageAvailable = isPackage ? isPackageAvailable(inventoryItem) : true;
            return (
              <div
                key={inventoryItem.id}
                className={`p-4 transition-colors cursor-pointer hover:bg-gray-50 ${selectedInventoryItem?.id === inventoryItem.id ? 'bg-emerald-50 border-r-4 border-emerald-500' : ''}`}
                onClick={() => {
                  handleInventoryClick(inventoryItem);

                  // Check if this is a package
                  const isPackage = inventoryItem.inventory || inventoryItem.inventories || inventoryItem.registryPackageType || inventoryItem.totalPrice !== undefined;

                  // Get coordinates: Use top-level for regular inventory, or first inventory for packages
                  let latitude = inventoryItem.latitude;
                  let longitude = inventoryItem.longitude;

                  if (isPackage && (!latitude || !longitude) && inventoryItem.inventories && inventoryItem.inventories.length > 0) {
                    latitude = inventoryItem.inventories[0].latitude;
                    longitude = inventoryItem.inventories[0].longitude;
                  }

                  if (latitude && longitude && !isNaN(parseFloat(latitude)) && !isNaN(parseFloat(longitude))) {
                    map.current?.flyTo({
                      center: [parseFloat(longitude), parseFloat(latitude)],
                      zoom: Math.max(map.current.getZoom(), 14),
                      duration: 1000
                    });

                    // Only open popup after navigation for regular inventory (not for package clicks)
                    if (!isPackage) {
                      const onMoveEnd = () => {
                        openPopupForMarker(inventoryItem);
                        map.current?.off('moveend', onMoveEnd);
                      };
                      map.current?.once('moveend', onMoveEnd);
                    }

                    updateMarkerAppearances(inventoryItem.id);
                  } else {
                    console.warn('MAP_VIEW-------No valid coordinates for inventoryItem:', inventoryItem.id, 'isPackage:', isPackage);
                  }
                }}
              >
                <div className="flex items-start space-x-3">
                  <img
                    src={
                      isPackage
                        ? (inventoryItem.thumbnail || inventoryItem.imageUrl || (inventoryItem.fullResImagePath && inventoryItem.fullResImagePath !== "null")
                          ? (inventoryItem.thumbnail || inventoryItem.imageUrl || inventoryItem.fullResImagePath)
                          : `${import.meta.env.VITE_APP_FOLDER_PATH}/nomedia.jpg`)
                        : (inventoryItem.fullResImagePath && inventoryItem.fullResImagePath !== "null"
                          ? inventoryItem.fullResImagePath
                          : `${import.meta.env.VITE_APP_FOLDER_PATH}/nomedia.jpg`)
                    }
                    alt={isPackage ? (inventoryItem.name) : (inventoryItem.displayName || inventoryItem.name)}
                    className="w-16 h-16 sm:w-20 sm:h-20 object-cover rounded-lg flex-shrink-0"
                    onError={(e: any) => {
                      e.currentTarget.src = `${import.meta.env.VITE_APP_FOLDER_PATH}/nomedia.jpg`;
                    }}
                  />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between mb-2">
                      <h4 
                        className="font-medium text-gray-900 text-sm sm:text-base pr-2 line-clamp-1"
                        title={isPackage ? (inventoryItem.name || 'Package Deal') : (inventoryItem.displayName || inventoryItem.name)}
                      >
                        {isPackage ? (inventoryItem.name || 'Package Deal') : (inventoryItem.displayName || inventoryItem.name)}
                      </h4>
                      <div className="flex space-x-1 flex-shrink-0">
                        {!isPackage && (inventoryItem.discountedPrice || inventoryItem.discountPercentage) && (
                          <Badge className="bg-red-500 text-white text-xs px-1.5 py-0.5">
                            {inventoryItem.discountPercentage || '10'}% OFF
                          </Badge>
                        )}
                        {!isPackage && isLoggedIn && (
                          <Badge className={`text-xs ${(!inventoryItem.availableBooking || !inventoryItem.active) ? 'bg-red-100 text-red-800' : 'bg-green-100 text-green-800'}`}>
                            {(!inventoryItem.availableBooking || !inventoryItem.active) ? 'Sold Out' : 'Available'}
                          </Badge>
                        )}
                        {!(inventoryItem.latitude && inventoryItem.longitude &&
                          !isNaN(parseFloat(inventoryItem.latitude)) && !isNaN(parseFloat(inventoryItem.longitude))) && (
                            <Badge className="bg-gray-100 text-gray-600 text-xs">
                              {isPackage ? `${inventoryItem.inventory?.total || 0} Locations` : 'No Location'}
                            </Badge>
                          )}
                      </div>
                    </div>

                   <p className="text-xs sm:text-sm text-gray-600 mb-1">
                    {isPackage
                      ? (() => {
                          const country = inventoryItem.country?.name || inventoryItem.trDistrictName || '';
                          const state = inventoryItem.state || inventoryItem.trStateName || '';
                          return `${country}${country && state ? ', ' : ''}${state}`;
                        })()
                      : (() => {
                          const district = inventoryItem.trDistrictName || '';
                          const state = inventoryItem.trStateName || '';
                          return `${district}${district && state ? ', ' : ''}${state}`;
                        })()
                    }
                  </p>

                    <p className="text-xs text-emerald-600 font-medium mb-2">
                      {isPackage
                        ? `by ${inventoryItem.publisher?.name || inventoryItem.mediaOwnerName || 'Publisher'}`
                        : `by ${inventoryItem.mediaOwnerName || 'N/A'}`
                      }
                    </p>

                    <div className="flex items-center justify-between">
                      <div className="text-left">
                        {isPackage ? (
                          <div className="flex flex-col">
                            <span className="text-sm font-bold text-emerald-600">
                              {(() => {
                                const price = inventoryItem.totalPrice || inventoryItem.price;
                                if (!price || price === 0) return 'N/A';
                                return `${inventoryItem?.currency || inventoryItem?.price?.localCurrencies?.code || currencyCode || "N/A"} ${Number(price).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
                              })()}
                            </span>
                            {inventoryItem.discount && inventoryItem.discount > 0 && (
                              <span className="text-xs text-gray-500 line-through">
                                {(() => {
                                  const price = inventoryItem.price;
                                  if (!price || price === 0) return 'N/A';
                                  return `${inventoryItem?.currency || inventoryItem?.price?.localCurrencies?.code || currencyCode || "N/A"} ${Number(price).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
                                })()}
                              </span>
                            )}
                          </div>
                        ) : inventoryItem.discountedPrice ? (
                          <div className="flex flex-col">
                            <span className="text-sm font-bold text-red-600">
                              {(() => {
                                const price = inventoryItem.discountedPrice;
                                if (!price || price === 0) return 'N/A';
                                return `${inventoryItem?.currency || inventoryItem?.price?.localCurrencies?.code || currencyCode || "N/A"} ${Number(price).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
                              })()}
                            </span>
                            <span className="text-xs text-gray-500 line-through">
                              {(() => {
                                const price = inventoryItem.currentPrice;
                                if (!price || price === 0) return 'N/A';
                                return `${inventoryItem?.currency || inventoryItem?.price?.localCurrencies?.code || currencyCode || "N/A"} ${Number(price).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
                              })()}
                            </span>
                          </div>
                        ) : (
                          <span className="text-sm font-bold text-emerald-600">
                            {(() => {
                              let price;
                              let suffix = '';

                              if (inventoryItem.type === 'DIGITAL') {
                                price = priceFormat === 'spot-based'
                                  ? inventoryItem.perSpotPrice
                                  : inventoryItem.perDayPrice;
                                suffix = priceFormat === 'spot-based' ? '/Spot' : '/Day';
                              } else {
                                price = (inventoryItem.sellingRate?.monthly?.month1 && inventoryItem.sellingRate.monthly.month1 > 0)
                                  ? inventoryItem.sellingRate.monthly.month1
                                  : inventoryItem.currentPrice;
                                suffix = '/Month';
                              }

                              if (!price || price === 0) return 'N/A';

                              return `${inventoryItem?.currency || inventoryItem?.price?.localCurrencies?.code || currencyCode || "N/A"} ${Number(price).toLocaleString('en-US', {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2
                              })}${suffix}`;
                            })()}
                          </span>

                        )}
                      </div>
                    </div>
                    {isPackage ? (
                      isLoggedIn ? (
                        <div className="flex items-center justify-between w-full text-xs text-gray-500 mb-3 mt-2">
                          <div className="flex items-center">
                            <Monitor className="mr-1 h-3 w-3" />
                            <span>{inventoryItem.inventory?.total ? `${inventoryItem.inventory.total} Locations` : 'Various'}</span>
                          </div>
                          <div className="flex items-center">
                            <Calendar className="mr-1 h-3 w-3" />
                            <span>{inventoryItem.campaignDuration ? `${inventoryItem.campaignDuration} days` : 'Fixed Period'}</span>
                          </div>
                        </div>
                      ) : (
                        <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 mb-3 mt-2">
                          <div className="flex items-center justify-center text-xs text-gray-600">
                            <Lock className="mr-2 h-3 w-3" />
                            <span>Login to view full audience and performance data</span>
                          </div>
                        </div>
                      )
                    ) : (
                      isLoggedIn ? (
                        <div className="flex items-center justify-between w-full text-xs text-gray-500 mb-3 mt-2">
                          <div className="flex items-center">
                            <Monitor className="mr-1 h-3 w-3" />
                            <span>
                              {inventoryItem.type === 'CLASSIC'
                                ? (inventoryItem.panelWidth && inventoryItem.panelHeight
                                  ? `${inventoryItem.panelWidth}x${inventoryItem.panelHeight}`
                                  : 'Standard')
                                : (inventoryItem.resolutionWidth && inventoryItem.resolutionHeight
                                  ? `${inventoryItem.resolutionWidth}x${inventoryItem.resolutionHeight}`
                                  : inventoryItem.specification?.resolution1Width && inventoryItem.specification?.resolution1Height
                                    ? `${inventoryItem.specification.resolution1Width}x${inventoryItem.specification.resolution1Height}`
                                    : 'Standard')}
                            </span>
                          </div>
                          <div className="flex items-center">
                            <Users className="mr-1 h-3 w-3" />
                            <span>{formatNumber(inventoryItem.impressionPerDay || 0)}</span>
                          </div>
                          <div className="flex items-center">
                            <Clock className="mr-1 h-3 w-3" />
                            <span>{inventoryItem.type === 'DIGITAL' ? '15s' : 'Monthly'}</span>
                          </div>
                        </div>
                      ) : (
                        <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 mb-3 mt-2">
                          <div className="flex items-center justify-center text-xs text-gray-600">
                            <Lock className="mr-2 h-3 w-3" />
                            <span>Login to view full audience and performance data</span>
                          </div>
                        </div>
                      )
                    )}
                    
                    <div className="flex items-center justify-between mt-3">
                      <div
                        className="flex items-center space-x-2"
                        style={selectedInventoryItem && selectedInventoryItem.id === inventoryItem.id ? { zIndex: 1001, position: 'relative' } : {}}
                      >
                        {isItemInCart && (
                          <Badge className="bg-emerald-600 text-white text-xs">
                            In Cart
                          </Badge>
                        )}
                      </div>
                      <div
                        className="flex space-x-1 sm:space-x-2"
                        style={selectedInventoryItem && selectedInventoryItem.id === inventoryItem.id ? { zIndex: 1001, position: 'relative' } : {}}
                      >
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleInventorySelect(inventoryItem);
                          }}
                          className="text-xs"
                        >
                          Details
                        </Button>
                        {!loadingCartItemIds?.has(inventoryItem.id) ? (
                          isItemInCart ? (
                            <Button
                              size="sm"
                              className="text-xs bg-red-600 hover:bg-red-700 text-white"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleCartAction(inventoryItem);
                              }}
                              disabled={isPackage && !packageAvailable}
                            >
                              Remove
                            </Button>
                          ) : (() => {
                            // Check if price is 0 or invalid
                            let price = 0;
                            if (isPackage) {
                              price = inventoryItem.totalPrice || inventoryItem.price || 0;
                            } else {
                              if (inventoryItem.discountedPrice && inventoryItem.discountedPrice > 0) {
                                price = inventoryItem.discountedPrice;
                              } else if (inventoryItem.type === 'DIGITAL') {
                                price = (priceFormat === 'spot-based'
                                  ? inventoryItem.perSpotPrice
                                  : inventoryItem.perDayPrice) || 0;
                              } else {
                                price = (inventoryItem.sellingRate?.monthly?.month1 && inventoryItem.sellingRate.monthly.month1 > 0)
                                  ? inventoryItem.sellingRate.monthly.month1
                                  : inventoryItem.currentPrice || 0;
                              }
                            }
                            
                            const isPriceZero = !price || price === 0;
                            const isDisabled = isPriceZero || (isPackage ? !packageAvailable : (isLoggedIn && (!inventoryItem.availableBooking || !inventoryItem.active)));
                            
                            return (
                            <Button
                              size="sm"
                              className={`text-xs ${isDisabled ? 'bg-gray-400 cursor-not-allowed' : 'bg-emerald-600 hover:bg-emerald-700'} text-white`}
                              onClick={(e) => {
                                e.stopPropagation();
                                handleCartAction(inventoryItem);
                              }}
                              disabled={isDisabled}
                            >
                              {isPriceZero
                                ? 'Unavailable'
                                : (isPackage
                                  ? (!packageAvailable ? 'UnAvailable' : 'Add to Cart')
                                  : (isLoggedIn && (!inventoryItem.availableBooking || !inventoryItem.active) ? 'Sold Out' : 'Add to Cart')
                                )
                              }
                            </Button>
                          );
                        })()
                        ) : (
                          <Button
                            size="sm"
                            className="text-xs"
                            disabled
                          >
                            <div className="animate-spin mr-2 h-4 w-4 border-2 border-b-transparent rounded-full" />
                            Processing...
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}